import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const FEATURES = [
  { icon: 'ri-window-line', title: 'WPF / WinForms / .NET MAUI', desc: 'Enterprise-grade Windows desktop apps built with Microsoft\'s latest frameworks — WPF for rich UI, WinForms for mature workflows, and .NET MAUI for cross-platform targets.' },
  { icon: 'ri-wifi-off-line', title: 'Offline-First Architecture', desc: 'Applications that function fully without internet — essential for field operations, factories, and environments with unreliable connectivity.' },
  { icon: 'ri-robot-line', title: 'AI-Powered Automation', desc: 'Embed machine learning inference directly into Windows applications for real-time predictions, OCR document processing, and intelligent workflows on-device.' },
  { icon: 'ri-device-line', title: 'Hardware Integration', desc: 'Seamless connections with printers, barcode scanners, RFID readers, scales, cameras, POS terminals, and industrial equipment via USB, serial, and network protocols.' },
  { icon: 'ri-user-settings-line', title: 'Active Directory & SSO', desc: 'Full Windows authentication support with Active Directory integration, SSO, group policy enforcement, and role-based access control.' },
  { icon: 'ri-shield-check-line', title: 'Enterprise Security & Compliance', desc: 'Data encryption at rest and in transit, audit logging, license management, and compliance with ISO/IEC 27001:2022 information security standards.' },
];

const USE_CASES = [
  { icon: 'ri-store-3-line', title: 'Inventory & ERP Systems', desc: 'Full ERP desktop apps with multi-warehouse support, procurement, and financial modules.' },
  { icon: 'ri-hospital-line', title: 'Healthcare Management', desc: 'HIPAA-compliant clinical management systems with patient records, scheduling, and billing.' },
  { icon: 'ri-ship-line', title: 'Maritime Operations', desc: 'Ship chandling, port management, and maritime supply chain desktop platforms.' },
  { icon: 'ri-building-2-line', title: 'Manufacturing MES', desc: 'Manufacturing execution systems with real-time production monitoring and QC tracking.' },
];

export default function WindowsApplicationsPage() {
  const featRef = useRef<HTMLElement>(null);
  const featInView = useInView(featRef);
  const useCaseRef = useRef<HTMLElement>(null);
  const useCaseInView = useInView(useCaseRef);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-amber-50/60 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Link to="/services" className="text-gray-400 text-sm hover:text-teal-600 transition-colors cursor-pointer">Services</Link>
                <i className="ri-arrow-right-s-line text-gray-300" />
                <span className="text-teal-600 text-sm font-medium">Windows Applications</span>
              </div>
              {/* <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">Windows Applications</span> */}
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
                Enterprise-Grade <span className="text-amber-500">Desktop Software</span>
              </h1>
              <p className="text-gray-500 text-xl leading-relaxed mb-8">
                Build powerful Windows desktop applications with deep OS integration, offline capability, and AI-assisted features for industries that rely on high-performance local processing and hardware control.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['.NET / WPF / MAUI', 'Offline-First', 'Hardware Integration', 'ISO Certified Quality'].map((b) => (
                  <span key={b} className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium px-4 py-1.5 rounded-full">{b}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2">
                  Start Your Project <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/portfolio" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-gray-700 font-semibold px-7 py-3.5 rounded-lg border border-gray-200 transition-colors">
                  View Portfolio
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=windows%20desktop%20application%20software%20interface%20modern%20enterprise%20business%20desktop%20app%20UI%20design%20with%20menus%20toolbars%20data%20grid%20tables%20dashboard%20on%20monitor%20screen%20professional%20dark%20theme%20amber%20accent%20clean%20software%20application&width=700&height=500&seq=win-app-hero-001&orientation=landscape"
                  alt="Windows Applications"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-teal-600 py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[{ val: '150+', label: 'Desktop Apps Delivered' }, { val: '25+', label: 'Years .NET Experience' }, { val: '50+', label: 'Enterprise Clients' }, { val: '98%', label: 'Client Satisfaction' }].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-extrabold text-white">{s.val}</div>
                <div className="text-teal-200 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section ref={featRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-14 transition-all duration-700 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Core Capabilities</span> */}
              <h2 className="text-4xl font-extrabold text-gray-900">What We Deliver in Every Desktop App</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-amber-200 hover:bg-amber-50/20 transition-all duration-500 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-xl mb-5">
                    <i className={`${f.icon} text-2xl`} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section ref={useCaseRef} className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-12 transition-all duration-700 ${useCaseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Use Cases</span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">Industries We Serve with Desktop Solutions</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {USE_CASES.map((uc, i) => (
                <div
                  key={uc.title}
                  className={`bg-white border border-gray-100 rounded-2xl p-7 hover:border-amber-200 transition-all duration-500 ${useCaseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-5">
                    <i className={`${uc.icon} text-2xl`} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{uc.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=team%20of%20software%20developers%20working%20on%20enterprise%20windows%20desktop%20application%20in%20modern%20tech%20office%20with%20multiple%20monitors%20showing%20code%20and%20application%20UI%20professional%20clean%20environment%20teal%20accent%20lighting&width=700&height=500&seq=win-team-001&orientation=landscape"
                  alt="Windows Development Team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">Why Choose Us</span> */}
              <h2 className="text-4xl font-extrabold text-gray-900 mb-5">25 Years Building Windows Software</h2>
              <p className="text-gray-500 text-base leading-relaxed mb-6">
                We have been building Windows applications since the dawn of the platform — before the cloud, before smartphones, when desktop was king. That deep experience means we understand the architecture, performance patterns, and enterprise pitfalls that newer developers miss.
              </p>
              <div className="flex flex-col gap-4 mb-8">
                {[
                  'Full lifecycle ownership: from spec to deployment to support',
                  'ISO 9001:2015 quality management on every project',
                  'ISO/IEC 27001:2022 information security compliance',
                  'Long-term maintenance and upgrade partnerships',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3 text-sm text-gray-600">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-500 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line font-bold text-base" />
                    </div>
                    {point}
                  </div>
                ))}
              </div>
              <Link to="/contact" className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors">
                Start Your Desktop Project <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Need a Powerful Windows Application?</h2>
            <p className="text-teal-100 text-base mb-8">25 years of .NET expertise, ISO-certified quality, and a dedicated team ready to deliver.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
